import { Link, NavLink, Outlet } from "react-router-dom";
import { ChevronDown, LogOut, Sun, Moon, Users, CreditCard, ShieldCheck, Home, UserCog } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAppTheme } from "../context/ThemeContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { CreditAlertBanner } from "./CreditAlertBanner";
import { EmailVerificationBanner } from "./EmailVerificationBanner";
import { Logo } from "./Logo";
import NoIndex from "./NoIndex";

export default function Layout() {
  const { user, workspaces, activeWorkspace, switchWorkspace, logout } = useAuth();
  const { theme, toggleTheme } = useAppTheme();

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${
      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
    }`;

  return (
    <div className="min-h-screen bg-background">
      <NoIndex />
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl shadow-sm shadow-black/[0.02]">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Link to="/app" className="flex items-center" data-testid="app-logo-link">
              <Logo className="h-8" />
            </Link>
            {workspaces.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="workspace-selector"
                    className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors duration-150"
                  >
                    {activeWorkspace?.name} <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {workspaces.map((w) => (
                    <DropdownMenuItem key={w.id} onClick={() => switchWorkspace(w.id)} data-testid={`workspace-option-${w.id}`}>
                      {w.name} <span className="ml-auto text-xs text-muted-foreground">{w.role}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <nav className="hidden items-center gap-1 md:flex">
            <NavLink to="/app" className={navLinkClass} end data-testid="nav-home-link">
              <Home className="h-4 w-4" /> Home
            </NavLink>
            <NavLink to="/app/team" className={navLinkClass} data-testid="nav-team-link">
              <Users className="h-4 w-4" /> Team
            </NavLink>
            <NavLink to="/app/billing" className={navLinkClass} data-testid="nav-billing-link">
              <CreditCard className="h-4 w-4" /> Billing
            </NavLink>
            <NavLink to="/app/account" className={navLinkClass} data-testid="nav-account-link">
              <UserCog className="h-4 w-4" /> Account
            </NavLink>
            {user?.is_superadmin && (
              <NavLink to="/app/superadmin" className={navLinkClass} data-testid="nav-superadmin-link">
                <ShieldCheck className="h-4 w-4" /> Super Admin
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-3">
            <span
              className={`hidden rounded-full px-2.5 py-1 text-xs font-medium capitalize sm:inline ${
                activeWorkspace?.role === "admin" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              }`}
              data-testid="user-role-badge"
            >
              {activeWorkspace?.role}
            </span>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className="rounded-lg p-2 text-muted-foreground hover:bg-muted transition-colors duration-150"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-destructive transition-colors duration-150"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <nav className="flex items-center gap-1 overflow-x-auto border-t border-border/60 px-4 py-2 md:hidden">
          <NavLink to="/app" className={navLinkClass} end data-testid="nav-home-link-mobile">Home</NavLink>
          <NavLink to="/app/team" className={navLinkClass}>Team</NavLink>
          <NavLink to="/app/billing" className={navLinkClass}>Billing</NavLink>
          <NavLink to="/app/account" className={navLinkClass} data-testid="nav-account-link-mobile">Account</NavLink>
          {user?.is_superadmin && <NavLink to="/app/superadmin" className={navLinkClass}>Super Admin</NavLink>}
        </nav>
      </header>
      <EmailVerificationBanner />
      <CreditAlertBanner />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
}
